import { useI18n, resolveDateLocale } from '../../i18n';
// ============================================
// AccessControlPage Component
// Store entry/exit events and shopping sessions
// ============================================

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, PageContainer, Pagination } from '../../components/shared';
import {
  useAccessEvents,
  useShoppingSessions,
  useAccessControlStats,
} from '../../hooks';
import type { AccessEvent, ShoppingSession, AccessResult, AccessMethod } from '@conai/shared-types';
import { extractList, extractCount } from '../../utils/apiList';

// ============================================
// Types
// ============================================

type ViewMode = 'events' | 'sessions';

interface EventItem {
  id: string;
  customerName: string;
  storeName: string;
  eventType: 'entry' | 'exit';
  result: AccessResult;
  accessMethod?: AccessMethod;
  timestamp: string;
}

interface SessionItem {
  id: string;
  customerName: string;
  storeName: string;
  startedAt: string;
  endedAt: string | null;
  durationMinutes: number | null;
  isActive: boolean;
}

// ============================================
// Helpers
// ============================================

/** Chuỗi có nội dung, hoặc `null` — để `??` rơi tiếp sang nguồn khác. */
function toText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toMinutes(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * Bug #206: `new Date('').toLocaleString()` in ra "Invalid Date" — một giá trị
 * KHÔNG có thật nằm chình ình trong bảng. Thiếu mốc thời gian thì hiện gạch
 * ngang, đúng quy ước ô trống của các bảng khác trong app.
 */
function formatTimestamp(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

/**
 * Mốc `period_start`/`period_end` phải đọc theo MÚI GIỜ CỦA STORE, không phải
 * của trình duyệt: BE cắt "hôm nay" theo store, admin ngồi múi giờ khác mà
 * format bằng giờ máy mình sẽ thấy khung ngày lệch và hiểu sai con số.
 *
 * `Store.timezone` nullable -> `timeZone: undefined` = rơi về giờ máy; lúc đó
 * nhãn phía trên cũng không dám nói tên thành phố nào (xem `timezoneCityLabel`).
 */
function formatPeriodBoundary(
  value: string | null,
  timeZone: string | null,
  locale: string
): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timeZone || undefined,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    // Timezone lạ / runtime không hỗ trợ: hiện giờ máy còn hơn để render nổ.
    return date.toLocaleString(locale);
  }
}

/** `Europe/Warsaw` -> `Warsaw`. Không có timezone thì trả `null`, đừng bịa. */
function timezoneCityLabel(timezone: string | null): string | null {
  if (!timezone) return null;
  const city = timezone.split('/').pop();
  if (!city) return null;
  return city.replace(/_/g, ' ');
}

// ============================================
// Stats Component
// ============================================

/**
 * `subtitle` giải thích ĐƠN VỊ của con số (người vs lượt), `tooltip` giữ phần
 * dài dòng — hai số vênh nhau (184 người / 203 lượt) là ĐÚNG, không phải lỗi.
 */
function StatsCard({
  title,
  value,
  color,
  subtitle,
  tooltip,
}: {
  title: string;
  value: string | number;
  color: string;
  subtitle?: string;
  tooltip?: string;
}) {
  return (
    <div
      className="rounded-xl bg-white p-5 shadow-sm border border-slate-100"
      title={tooltip}
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <p className={`mt-2 text-lg font-bold sm:text-2xl ${color}`}>{value}</p>
      {subtitle && (
        <p className="mt-1 text-xs leading-snug text-slate-400">{subtitle}</p>
      )}
    </div>
  );
}

// ============================================
// View Mode Toggle
// ============================================

function ViewToggle({ mode, onChange }: { mode: ViewMode; onChange: (mode: ViewMode) => void }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex rounded-lg bg-slate-100 p-1">
      <button
        onClick={() => onChange('events')}
        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
          mode === 'events'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {t('accessEvents')}
      </button>
      <button
        onClick={() => onChange('sessions')}
        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
          mode === 'sessions'
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {t('shoppingSessions')}
      </button>
    </div>
  );
}

// ============================================
// Badge Components
// ============================================

function EventTypeBadge({ type }: { type: 'entry' | 'exit' }) {
  const { t } = useI18n();
  const styles = type === 'entry'
    ? 'bg-green-100 text-green-700'
    : 'bg-blue-100 text-blue-700';
  const label = type === 'entry' ? t('entry') : t('exit');
  
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles}`}>
      {label}
    </span>
  );
}

function ResultBadge({ result }: { result: AccessResult }) {
  const { t } = useI18n();
  const config: Record<AccessResult, { bg: string; text: string; label: string }> = {
    SUCCESS: { bg: 'bg-green-100', text: 'text-green-700', label: t('success') },
    DENIED: { bg: 'bg-red-100', text: 'text-red-700', label: t('denied') },
    EXPIRED: { bg: 'bg-orange-100', text: 'text-orange-700', label: t('expired') },
    INVALID: { bg: 'bg-red-100', text: 'text-red-700', label: t('invalid') },
  };
  const style = config[result] || config.INVALID;
  
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

function SessionStatusBadge({ isActive }: { isActive: boolean }) {
  const { t } = useI18n();
  return isActive ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
      <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
      {t('inStore')}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
      {t('completed')}
    </span>
  );
}

// ============================================
// Events Table Component
// ============================================

function EventsTable({ data, isLoading }: { data: EventItem[]; isLoading: boolean }) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8 text-center">
        <p className="text-slate-500">{t('noAccessEventsFound')}</p>
      </div>
    );
  }

  return (
    // Bug #162: bọc overflow-x-auto + min-w để bảng scroll ngang trên mobile thay vì vỡ layout
    <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="min-w-[640px] w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('accesscontrolCustomer')}</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('accesscontrolStore')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('eventType')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('result')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('timestamp')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4">
                <span className="font-medium text-slate-900">{item.customerName}</span>
              </td>
              <td className="px-6 py-4 text-slate-600">{item.storeName}</td>
              <td className="px-6 py-4 text-center">
                <EventTypeBadge type={item.eventType} />
              </td>
              <td className="px-6 py-4 text-center">
                <ResultBadge result={item.result} />
              </td>
              <td className="px-6 py-4 text-center text-sm text-slate-500">
                {formatTimestamp(item.timestamp)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ============================================
// Sessions Table Component
// ============================================

function SessionsTable({ data, isLoading }: { data: SessionItem[]; isLoading: boolean }) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8 text-center">
        <p className="text-slate-500">{t('noShoppingSessionsFound')}</p>
      </div>
    );
  }

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    // Bug #162: bọc overflow-x-auto + min-w để bảng scroll ngang trên mobile thay vì vỡ layout
    <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="min-w-[760px] w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('accesscontrolCustomer')}</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('accesscontrolStore')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('accesscontrolStatus')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('entryTime')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('exitTime')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('duration')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4">
                <span className="font-medium text-slate-900">{item.customerName}</span>
              </td>
              <td className="px-6 py-4 text-slate-600">{item.storeName}</td>
              <td className="px-6 py-4 text-center">
                <SessionStatusBadge isActive={item.isActive} />
              </td>
              <td className="px-6 py-4 text-center text-sm text-slate-500">
                {formatTimestamp(item.startedAt)}
              </td>
              <td className="px-6 py-4 text-center text-sm text-slate-500">
                {formatTimestamp(item.endedAt)}
              </td>
              <td className="px-6 py-4 text-center text-sm font-medium text-slate-700">
                {formatDuration(item.durationMinutes)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function AccessControlPage() {
  const { t, language } = useI18n();
  const dateLocale = resolveDateLocale(language);
  // Bug #161: thông báo giỏ hàng ("Basket updated") điều hướng tới đây kèm
  // `?view=sessions`. Không đọc param thì người dùng rơi vào tab Events và tự
  // đi tìm phiên mua — tức là vẫn "bấm thông báo mà không tới nơi cần tới".
  const [searchParams] = useSearchParams();
  const viewFromQuery: ViewMode | null =
    searchParams.get('view') === 'sessions'
      ? 'sessions'
      : searchParams.get('view') === 'events'
        ? 'events'
        : null;
  const [viewMode, setViewMode] = useState<ViewMode>(viewFromQuery ?? 'events');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Điều hướng lại vào trang khi đang đứng sẵn ở đây (bấm thông báo thứ hai)
  // không remount component, nên phải đồng bộ theo param. Chỉ chạy khi param
  // đổi — người dùng tự bấm tab sau đó vẫn giữ nguyên lựa chọn của họ.
  useEffect(() => {
    if (!viewFromQuery) return;
    setViewMode(viewFromQuery);
    setCurrentPage(1);
  }, [viewFromQuery]);

  // Reset page when view changes
  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    setCurrentPage(1);
  };

  // API Queries
  const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useAccessEvents({
    page: currentPage,
    page_size: pageSize,
  });

  const { data: sessionsData, isLoading: sessionsLoading, error: sessionsError } = useShoppingSessions({
    page: currentPage,
    page_size: pageSize,
  });

  // Không truyền storeId -> BE tổng hợp mọi store user đang quản lý và tự
  // enforce phạm vi. (Trước đây truyền `undefined` vào 3 query có
  // `enabled: !!storeId` nên chúng bị disable vĩnh viễn -> 4 thẻ luôn 0.)
  const {
    stats,
    timezone: statsTimezone,
    periodStart,
    periodEnd,
    storeCount,
    hasStats,
    isForbidden: statsForbidden,
    isUnavailable: statsUnavailable,
    isLoading: statsLoading,
  } = useAccessControlStats();

  // Map events
  //
  // Bug #206: đọc thẳng `.results` là sai — api-client chỉ dựng `{results}` khi
  // BE trả kèm `meta`, không thì FE nhận MẢNG TRẦN và bảng hiện "không có dữ
  // liệu" dù API trả đủ. Mọi chỗ đọc danh sách phải đi qua `extractList`.
  const events: EventItem[] = useMemo(() => {
    return extractList<AccessEvent>(eventsData).map((e: AccessEvent) => {
      const raw = e as unknown as Record<string, unknown>;
      return {
        id: String(e.id ?? ''),
        customerName: e.customer?.full_name ?? toText(raw.customer_name) ?? t('guest'),
        storeName: e.store?.name ?? toText(raw.store_name) ?? t('unknownStore'),
        eventType: e.event_type,
        result: e.result,
        timestamp: e.created_at ?? (toText(raw.occurred_at) || ''),
      };
    });
  }, [eventsData, t]);

  // Map sessions — DỮ LIỆU THẬT từ `GET /access/sessions/` (useShoppingSessions
  // -> AccessControlService.listSessions). Không có mảng mock nào ở màn này; khi
  // endpoint chưa trả gì thì `SessionsTable` hiện empty state chuẩn của app
  // ("noShoppingSessionsFound"), TUYỆT ĐỐI không bịa dòng để lấp bảng.
  //
  // Tên field nhận cả alias theo cột DB (`entered_at`/`exited_at`) vì serializer
  // của BE phát ra tên nào cũng có tiền lệ — xem ghi chú ở
  // packages/shared-types/src/session.types.ts.
  const sessions: SessionItem[] = useMemo(() => {
    return extractList<ShoppingSession>(sessionsData).map((s: ShoppingSession) => {
      const raw = s as unknown as Record<string, unknown>;
      const startedAt = toText(raw.started_at) ?? toText(raw.entered_at) ?? '';
      const endedAt = toText(raw.ended_at) ?? toText(raw.exited_at) ?? null;
      return {
        id: String(s.id ?? ''),
        customerName: s.customer?.full_name ?? toText(raw.customer_name) ?? t('guest'),
        storeName: s.store?.name ?? toText(raw.store_name) ?? t('unknownStore'),
        startedAt,
        endedAt,
        durationMinutes: toMinutes(raw.duration_minutes),
        isActive: !endedAt,
      };
    });
  }, [sessionsData, t]);

  // Current data based on view mode
  const isLoading = viewMode === 'events' ? eventsLoading : sessionsLoading;
  const error = viewMode === 'events' ? eventsError : sessionsError;
  // Cùng lý do với `extractList`: payload không có `meta` thì không có `count`,
  // đọc thẳng `.count` ra 0 -> phân trang biến mất dù còn trang sau. Không có
  // tổng từ server thì lấy số dòng của trang hiện tại làm mốc.
  const totalItems = viewMode === 'events'
    ? extractCount(eventsData, events.length)
    : extractCount(sessionsData, sessions.length);
  const totalPages = Math.ceil(totalItems / pageSize);

  // ============================================
  // Stats — nhãn múi giờ, khung thời gian, các trạng thái không-có-số
  // ============================================

  const timezoneCity = timezoneCityLabel(statsTimezone);
  const periodStartText = formatPeriodBoundary(periodStart, statsTimezone, dateLocale);
  const periodEndText = formatPeriodBoundary(periodEnd, statsTimezone, dateLocale);
  const periodRangeText =
    periodStartText && periodEndText
      ? t('statsPeriodRange', { start: periodStartText, end: periodEndText })
      : null;
  // "Hôm nay" của ai? BE cắt ngày theo giờ STORE, còn admin có thể ngồi múi giờ
  // khác — phải ghi rõ ra thay vì để người dùng đoán.
  const todayScopeLabel = timezoneCity
    ? t('todayInTimezone', { timezone: timezoneCity })
    : t('todayInStoreLocalTime');

  // `store_count === 0` = user CHƯA được gán store nào. Hiện 4 số 0 ở đây trông
  // y hệt "cửa hàng vắng khách" — hai tình huống khác hẳn nhau.
  const noStoresAssigned = hasStats && storeCount === 0;
  const statsNumbersUsable = hasStats && !statsForbidden && !statsUnavailable;
  // 403 = ngoài phạm vi HOẶC store không tồn tại (BE cố ý không phân biệt để
  // không dò được store nào có thật) -> chỉ nói chung chung, không đoán thêm.
  // 404 = bản BE đang chạy chưa có endpoint stats -> "chưa có dữ liệu", KHÔNG
  // phải màn hình lỗi; Sessions/Events bên dưới vẫn chạy bình thường.
  const statValue = (value: number): string =>
    statsLoading ? '...' : statsNumbersUsable ? value.toLocaleString(dateLocale) : '—';
  const statSubtitle = (ownSubtitle: string): string | undefined => {
    if (statsLoading) return undefined;
    if (statsNumbersUsable) return ownSubtitle;
    // 403 đã có banner riêng ngay trên lưới thẻ, không lặp lại 4 lần.
    return statsForbidden ? undefined : t('statsNoDataYet');
  };
  /** Gắn khung ngày vào tooltip của các thẻ TÍNH THEO NGÀY. */
  const withPeriod = (text: string): string =>
    periodRangeText ? `${text}\n${periodRangeText}` : text;

  return (
    <PageContainer>
      <PageHeader
        title={t('accessControl')}
        subtitle={t('monitorStoreEntriesExitsAndShopping')}
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadAccessControlData')}</p>
        </div>
      )}

      {/* Stats Row */}
      <div className="mt-6">
        {statsNumbersUsable && !noStoresAssigned && (
          <p
            className="mb-3 text-xs text-slate-500"
            title={periodRangeText ?? undefined}
          >
            <span className="font-medium text-slate-600">{todayScopeLabel}</span>
            {periodRangeText && (
              <span className="ml-1 text-slate-400">· {periodRangeText}</span>
            )}
          </p>
        )}

        {statsForbidden && (
          <div className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            {t('storeAccessForbidden')}
          </div>
        )}

        {noStoresAssigned ? (
          <div className="rounded-xl border border-slate-100 bg-white p-8 text-center shadow-sm">
            <p className="text-sm font-medium text-slate-700">{t('noStoresAssigned')}</p>
            <p className="mt-1 text-xs text-slate-500">{t('noStoresAssignedHint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title={t('todaysVisitors')}
              value={statValue(stats.todayVisitors)}
              color="text-purple-700"
              subtitle={statSubtitle(t('uniquePeopleLabel'))}
              tooltip={withPeriod(t('todaysVisitorsTooltip'))}
            />
            {/* Số SỐNG, không lọc theo ngày -> tuyệt đối không gắn nhãn "hôm
                nay" và không kèm khung thời gian vào tooltip. */}
            <StatsCard
              title={t('currentlyInStore')}
              value={statValue(stats.currentlyInStore)}
              color="text-green-600"
              subtitle={statSubtitle(t('liveNowLabel'))}
              tooltip={t('currentlyInStoreTooltip')}
            />
            <StatsCard
              title={t('todaysEntries')}
              value={statValue(stats.todayEntries)}
              color="text-blue-600"
              subtitle={statSubtitle(t('entryScansLabel'))}
              tooltip={withPeriod(t('todaysEntriesTooltip'))}
            />
            <StatsCard
              title={t('todaysExits')}
              value={statValue(stats.todayExits)}
              color="text-slate-600"
              subtitle={statSubtitle(t('exitScansLabel'))}
              tooltip={withPeriod(t('todaysExitsTooltip'))}
            />
          </div>
        )}
      </div>

      {/* View Toggle */}
      <div className="mt-6">
        <ViewToggle mode={viewMode} onChange={handleViewChange} />
      </div>

      {/* Table */}
      <div className="mt-6">
        {viewMode === 'events' ? (
          <EventsTable data={events} isLoading={isLoading} />
        ) : (
          <SessionsTable data={sessions} isLoading={isLoading} />
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </PageContainer>
  );
}
